import { useRef, useState } from "react";

import { AlertTriangleIcon, Button, ButtonStyle, Form, FormField, InlineText, Modal } from "@prequel/shared-components";

import { DataDestination } from "@/types";

type ConfirmDataDestinationModalProps = {
  kind: "GENERATE_LINK" | "ADD_DESTINATION";
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  dataIdentifier: string;
  dataDestination: DataDestination;
  onConfirm: () => void;
};
const ConfirmDataDestinationModal = ({
  kind,
  show,
  setShow,
  dataIdentifier,
  dataDestination,
  onConfirm,
}: ConfirmDataDestinationModalProps) => {
  const [submitEnabled, setSubmitEnabled] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const validate = (value: string) => setSubmitEnabled(value === dataIdentifier);

  const onChange = (value: string) => {
    setInputValue(value);
    validate(value);
  };

  const onClose = (show: boolean) => {
    setInputValue("");
    setShow(show);
    setSubmitEnabled(false);
  };

  const buttonRef = useRef<HTMLButtonElement>(null);

  return (
    <Modal open={show} onClose={onClose}>
      <div>
        <div className="flex-column flex">
          <div className="mr-4 flex size-10 items-center justify-center rounded-full bg-red-100">
            <AlertTriangleIcon
              className="size-6 text-red-400"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            />
          </div>
          <div>
            <Modal.Title as="h3" className="text-xl/6 font-bold text-gray-900">
              Send data to new destination
            </Modal.Title>
            <span className="text-sm text-gray-500">Please confirm the following details.</span>
          </div>
        </div>
        <div className="mt-2 px-14 text-sm font-semilight text-gray-500">
          <div className="mb-2">
            You are about to send data from: <br />
            <InlineText value={dataIdentifier} /> <br />
            to <br />
            {dataDestination.host && <InlineText value={dataDestination.host} />}
            {dataDestination.host && dataDestination.bucket_name && (
              <>
                {" via the bucket"} <br />
              </>
            )}
            {dataDestination.bucket_name && <InlineText value={dataDestination.bucket_name} />}
          </div>
          <p>
            {`Before you
            ${
              kind === "GENERATE_LINK" ? "generate this link" : "add this destination"
            }, please be sure that the organization ID and host are associated
            with the same account.`}
          </p>
        </div>
      </div>
      {/* divider */}
      <div className="-mx-6 my-3 h-px w-lg bg-gray-200"></div>
      {/* Assigning "onConfirm" to "onSubmit" here will allow the user to leverage the browser defaults (like using "Enter" to confirm form submission)*/}
      {/* Assigning the "buttonRef" to "submitButtonRef" here will allow Form level "hotkeys" (e.g., "Cmd+Enter") to simulate a submission */}
      <Form className="px-8" onSubmit={onConfirm} submitButtonRef={buttonRef}>
        <div className="mb-3 px-8 text-center">
          <p className="text-sm text-gray-500">
            {"Please type "}
            <span className="font-bold text-gray-800">{dataIdentifier}</span>
            {" to confirm."}
          </p>
        </div>
        <FormField id="organization_id" type="text" value={inputValue} onChangeHandler={onChange} />
        <Button
          text={`The details above are correct, ${kind === "GENERATE_LINK" ? "generate link" : "add destination"}`}
          className="mt-3 w-full"
          type={ButtonStyle.DANGER}
          disabled={!submitEnabled}
          submit // By making this button a "Submit" button, the user can click this button to "Submit" the form, to which we've provided the "onConfirm" function at the form level
          ref={buttonRef}
        />
      </Form>
    </Modal>
  );
};

export default ConfirmDataDestinationModal;
